/**
 * #605 — the browser's calls to the first-party handoff routes
 * (`/api/handoff/*`): the Papan HOST begins a request for the app it runs and
 * collects the answer; the PICKER PAGE describes, measures, answers or
 * cancels it. Both run as the signed-in user, in the tenant the page browses.
 */
import { getApiBase, getDefaultHeaders, request, recordActingIdentity, currentAccount } from '@papan/api-client'
import type {
  HandoffCapRefusal,
  HandoffFileFacts,
  HandoffMeasure,
  HandoffNameProbe,
  HandoffOutcome,
  HandoffRequestView,
  HandoffSaveResult,
  HandoffSaveStatus,
  HandoffSaveTargets,
} from '@papan/types'

/** A redeemed drop: a read grant and its files' facts, or why not. */
export type HandoffDropResult =
  /** `folders`: the names of the folders dragged, as the person saw them —
   *  absent when only files were, and from an older server. */
  | { ok: true; grant: string; files: HandoffFileFacts[]; folders?: string[] }
  | Exclude<HandoffMeasure, { ok: true }>

export type HandoffAnswerResult =
  | { ok: true; answered: true }
  | Exclude<HandoffMeasure, { ok: true }>

const json = (body: unknown) => ({
  method: 'POST' as const,
  body: JSON.stringify(body),
  headers: { 'Content-Type': 'application/json' },
})

/** A fresh 256-bit id (base64url, 43 chars) — a drag's token. */
export function newHandoffId(): string {
  const bytes = crypto.getRandomValues(new Uint8Array(32))
  let bin = ''
  for (const b of bytes) bin += String.fromCharCode(b)
  return btoa(bin).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '')
}

export const handoffApi = {
  /** Host: begin a read request for the app whose serve session is `sessionId`. */
  begin(sessionId: string, options: unknown): Promise<{ pid: string }> {
    return request('/api/handoff/requests', json({ sessionId, kind: 'read', options }))
  },

  /** Host: begin a save request — the app's options and its byte count.
   *  Rejects with `status` 413 when the bytes are over Papan's ceiling. */
  beginSave(sessionId: string, options: unknown, size: unknown): Promise<{ pid: string }> {
    return request('/api/handoff/requests', json({ sessionId, kind: 'save', options, size }))
  },

  /** Host: what became of a request it began. */
  outcome(pid: string): Promise<HandoffOutcome> {
    return request(`/api/handoff/requests/${encodeURIComponent(pid)}/outcome`)
  },

  /** Picker page: what the request asks for. Rejects (404) when it is gone. */
  describe(pid: string): Promise<HandoffRequestView> {
    return request(`/api/handoff/requests/${encodeURIComponent(pid)}`)
  },

  /** Picker page: what confirming these would transfer — nothing is granted. */
  measure(pid: string, ids: string[]): Promise<HandoffMeasure> {
    return request(`/api/handoff/requests/${encodeURIComponent(pid)}/read`, json({ ids, dryRun: true }))
  },

  /** Picker page: answer the request with these. Rejects (409) when it is no longer open. */
  answerRead(pid: string, ids: string[]): Promise<HandoffAnswerResult> {
    return request(`/api/handoff/requests/${encodeURIComponent(pid)}/read`, json({ ids }))
  },

  /**
   * Drive: register a drag as the person, so an app it is dropped on can ask
   * for its content. Fire-and-forget: the drag must not wait on it. NOT
   * `keepalive` — a drag never unloads the page, and a keepalive body is
   * capped at 64 KiB (a big selection of long ids would silently not land).
   */
  registerDrag(token: string, ids: string[]): void {
    const sent = { ...getDefaultHeaders(), 'Content-Type': 'application/json' }
    const sentAccount = currentAccount()   // #1316: the account this is sent under
    void fetch(`${getApiBase()}/api/handoff/drags`, {
      method: 'POST',
      credentials: 'include',
      headers: sent,
      body: JSON.stringify({ token, ids }),
    }).then((res) => recordActingIdentity(res, sent, sentAccount), () => {})
  },

  /** Host: redeem a drop onto the app whose serve session is `sessionId`. */
  drop(sessionId: string, token: string, options: unknown): Promise<HandoffDropResult> {
    return request('/api/handoff/drops', json({ sessionId, token, options }))
  },

  /** Save picker: which of these folders take a save (a hint — the save re-checks). */
  saveTargets(pid: string, ids: string[]): Promise<HandoffSaveTargets> {
    return request(`/api/handoff/requests/${encodeURIComponent(pid)}/save-targets`, json({ ids }))
  },

  /** Save picker: what saving under `name` in `parentId` would do. */
  probeName(pid: string, parentId: string | null, name: string): Promise<{ name: HandoffNameProbe }> {
    return request(`/api/handoff/requests/${encodeURIComponent(pid)}/save-probe`, json({ parentId, name }))
  },

  /** Save picker: what saving these names (a save of several) in `parentId` would do, each. */
  probeNames(pid: string, parentId: string | null, names: string[]): Promise<{ names: HandoffNameProbe[] }> {
    return request(`/api/handoff/requests/${encodeURIComponent(pid)}/save-probe`, json({ parentId, names }))
  },

  /** Save picker: answer with where and under what name (none for a save of
   *  several — each keeps its own). Rejects (409) when the request is no longer open. */
  answerSave(pid: string, choice: { parentId: string | null; name?: string; replace: boolean }): Promise<HandoffSaveResult> {
    return request(`/api/handoff/requests/${encodeURIComponent(pid)}/save`, json(choice))
  },

  /** Save picker: how the save the person answered stands. */
  saveStatus(pid: string): Promise<HandoffSaveStatus> {
    return request(`/api/handoff/requests/${encodeURIComponent(pid)}/save-status`)
  },

  /**
   * Picker page / host: the user gave up. `keepalive` so it still lands when
   * the page is closing (a popup's `pagehide`) — `sendBeacon` would drop the
   * tenant headers the route needs. The WAIT is bounded, not the request:
   * whoever waits (the page reporting its Cancel) goes on after
   * `CANCEL_WAIT_MS`, while a slow cancel still lands — a keepalive request
   * outlives the popup that closes meanwhile.
   *
   * Resolves whether the server HAS it: a cancel lost on the way (the network,
   * a hiccup) is sent again a few times, and one that never landed says so —
   * the picker must not tell the app "cancelled" while the request stays open
   * (a popup's app would wait out its whole life).
   */
  cancel(pid: string, refusal?: HandoffCapRefusal): Promise<boolean> {
    const url = `${getApiBase()}/api/handoff/requests/${encodeURIComponent(pid)}/cancel`
    // Each attempt is bounded: an unbounded one on a hanging connection (the
    // mobile case this retry exists for) outlives `CANCEL_WAIT_MS`, so the
    // caller is told "couldn't send" while the cancel lands a minute later —
    // the app hears `cancelled` off a picker still saying it failed.
    const cancelHeaders = { ...getDefaultHeaders(), 'Content-Type': 'application/json' }
    const cancelHeadersAccount = currentAccount()   // #1316: the account this is sent under
    const once = () => fetch(url, {
      method: 'POST',
      credentials: 'include',
      keepalive: true,
      headers: cancelHeaders,
      body: JSON.stringify(refusal ? { refusal } : {}),
      signal: AbortSignal.timeout(CANCEL_ATTEMPT_MS),
    }).then((res) => { recordActingIdentity(res, cancelHeaders, cancelHeadersAccount); return res.status }, () => 0)
    const sent = (async () => {
      for (let attempt = 0; ; attempt++) {
        const status = await once()
        // Landed — or the request is already settled (409): either way the
        // server is not waiting on this "no".
        if ((status >= 200 && status < 300) || status === 409 || status === 404) return true
        const transient = status === 0 || status === 408 || status === 429 || status >= 500
        if (!transient || attempt >= CANCEL_RETRIES) return false
        await new Promise((r) => setTimeout(r, 500 * 2 ** attempt))
      }
    })()
    let timer: ReturnType<typeof setTimeout> | undefined
    const waited = new Promise<boolean>((resolve) => { timer = setTimeout(() => resolve(false), CANCEL_WAIT_MS) })
    return Promise.race([sent, waited]).finally(() => clearTimeout(timer))
  },
}

/** How long a cancel's caller waits on it before going on. */
const CANCEL_WAIT_MS = 10_000
/** How long ONE attempt may take. Four of these plus the backoff between them
 *  (0.5 + 1 + 2 s) stay inside `CANCEL_WAIT_MS`. */
const CANCEL_ATTEMPT_MS = 1_500
/** How many times a cancel lost on the way is sent again. */
const CANCEL_RETRIES = 3
